import axiosInstance from '@/lib/axios';

export type TemplateLibraryCategoryKey = 'utility' | 'marketing' | 'authentication';

export interface TemplateComponentButton {
  type: string;
  text?: string;
  url?: string;
  phone_number?: string;
  otp_type?: string;
}

export interface TemplateComponentNode {
  type: 'header' | 'body' | 'footer' | 'buttons' | string;
  format?: 'text' | 'image' | 'document' | 'video' | 'location' | string;
  text?: string;
  buttons?: TemplateComponentButton[];
  add_security_recommendation?: boolean;
  code_expiration_minutes?: number;
}

export interface TemplateMetaPayload {
  name?: string;
  language?: string;
  category?: string;
  parameter_format?: string;
  components?: TemplateComponentNode[];
  templateLibraryIndustryIds?: string[];
  templateLibrarySubcategoryIds?: string[];
}

export interface TemplateLibraryTemplateNode {
  key: string;
  label: string;
  count: number;
  content: string;
  metaPayload?: TemplateMetaPayload;
  categoryKey?: string;
  industryKey?: string;
  subcategoryKey?: string;
}

export interface TemplateLibrarySubcategoryNode {
  id: string;
  key: string;
  label: string;
  count: number;
  templates: TemplateLibraryTemplateNode[];
}

export interface TemplateLibraryIndustryNode {
  id: string;
  key: string;
  label: string;
  count: number;
  subcategories: TemplateLibrarySubcategoryNode[];
}

export interface TemplateLibraryCategoryNode {
  key: TemplateLibraryCategoryKey;
  label: string;
  count: number;
  industries: TemplateLibraryIndustryNode[];
}

export interface TemplateLibraryFiltersResponse {
  categories: TemplateLibraryCategoryNode[];
}

export interface TemplateLibraryListResponse {
  templates: TemplateLibraryTemplateNode[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface TemplateLibraryFilters {
  categoryKey?: TemplateLibraryCategoryKey;
  industryKeys?: string[];
  subcategoryKeys?: string[];
  search?: string;
  page?: number;
  perPage?: number;
}

export const templateLibraryService = {
  getTemplateLibraryFilters: async (): Promise<TemplateLibraryFiltersResponse> => {
    const response = await axiosInstance.get<{ data: TemplateLibraryFiltersResponse }>(
      `/wp-crm/templates/library/filters`
    );
    return response.data.data;
  },

  getTemplateLibraryList: async (
    filters: TemplateLibraryFilters = {}
  ): Promise<TemplateLibraryListResponse> => {
    const searchParams = new URLSearchParams();
    if (filters.categoryKey) {
      searchParams.set('category', filters.categoryKey);
    }
    if (filters.industryKeys?.length) {
      searchParams.set('industry', filters.industryKeys.join(','));
    }
    if (filters.subcategoryKeys?.length) {
      searchParams.set('subcategory', filters.subcategoryKeys.join(','));
    }
    if (filters.search) {
      searchParams.set('search', filters.search);
    }
    if (filters.page) {
      searchParams.set('page', filters.page.toString());
    }
    if (filters.perPage) {
      searchParams.set('perPage', filters.perPage.toString());
    }

    const response = await axiosInstance.get<{ data: TemplateLibraryListResponse }>(
      `/wp-crm/templates/library/list${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    );
    return response.data.data;
  },
};
